import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import format from 'date-fns/format';

type ScheduledCallData = {
  htmlLink?: string;
  status?: string;
  summary?: string;
  start?: { dateTime: string };
  end?: { dateTime: string };
  attendees?: Array<{ email: string; self?: boolean }>;
};

type LeadHeaderScheduledCallProps = {
  data?: ScheduledCallData | null;
  variant?: 'banner' | 'chip';
};

export default function LeadHeaderScheduledCall({
  data,
  variant = 'banner',
}: LeadHeaderScheduledCallProps) {
  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  const startLabel = data.start?.dateTime
    ? format(new Date(data.start.dateTime), 'do MMM yyyy p')
    : null;

  if (variant === 'chip') {
    return (
      <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-medium text-sky-800 ring-1 ring-sky-200/80">
        <CalendarDaysIcon className="h-3 w-3 flex-shrink-0 text-sky-600" />
        <span className="truncate">
          {startLabel ? (
            <>
              Call · <time dateTime={data.start!.dateTime}>{startLabel}</time>
            </>
          ) : (
            'Scheduled call'
          )}
          {data.status ? ` · ${data.status.toLowerCase()}` : ''}
        </span>
        {data.htmlLink ? (
          <a
            rel="noreferrer"
            target="_blank"
            href={data.htmlLink}
            className="flex-shrink-0 font-medium text-sky-700 underline hover:text-sky-900"
          >
            Open
          </a>
        ) : null}
      </span>
    );
  }

  return (
    <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-sky-900">
      <CalendarDaysIcon
        className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-sky-600"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium leading-snug">
          Scheduled call
          {startLabel ? (
            <>
              : <time dateTime={data.start!.dateTime}>{startLabel}</time>
            </>
          ) : null}
          {data.htmlLink ? (
            <>
              {' · '}
              <a
                rel="noreferrer"
                target="_blank"
                href={data.htmlLink}
                className="text-sky-700 underline hover:text-sky-800"
              >
                View in calendar
              </a>
            </>
          ) : null}
        </p>
        {data.summary ? (
          <p className="mt-0.5 truncate text-[11px] text-sky-800">
            {data.summary}
          </p>
        ) : null}
      </div>
    </div>
  );
}
